import { format } from 'date-fns';
import { AlertTriangle, Activity, Clock, MapPin, Gauge, TrendingUp, Zap } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

interface DeviceStatusBarProps {
  device: any;
  isOnline: boolean;
  alertCount: number;
  sensors: any[];
}

export function DeviceStatusBar({ device, isOnline, alertCount, sensors }: DeviceStatusBarProps) {
  const activeSensors = sensors.filter((s) => s.latest_data).length;
  const totalSensors = sensors.length;

  // Find the most recent update time across all sensors
  const lastUpdateTime =
    sensors
      .filter((sensor) => sensor.latest_data?.timestamp)
      .map((sensor) => new Date(sensor.latest_data.timestamp))
      .sort((a, b) => b.getTime() - a.getTime())[0] || null;

  const getHealthStatus = () => {
    if (!isOnline) return { text: 'Offline', color: 'text-red-500', bg: 'bg-red-500' };
    if (alertCount > 0) return { text: 'Issues Detected', color: 'text-yellow-500', bg: 'bg-yellow-500' };
    if (activeSensors === totalSensors && totalSensors > 0)
      return { text: 'Healthy', color: 'text-green-500', bg: 'bg-green-500' };
    return { text: 'Partial Data', color: 'text-blue-500', bg: 'bg-blue-500' };
  };

  const healthStatus = getHealthStatus();

  return (
    <Card className="border-l-4 border-l-primary shadow-sm">
      <CardContent className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {/* Device Status */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Activity className="h-4 w-4" />
              Device Status
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${healthStatus.bg}`}></div>
              <span className={`font-semibold ${healthStatus.color}`}>{healthStatus.text}</span>
            </div>
          </div>

          {/* Location */}
          {device.location_name && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <MapPin className="h-4 w-4" />
                Location
              </div>
              <div className="font-semibold">{device.location_name}</div>
            </div>
          )}

          {/* Sensor Status */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Gauge className="h-4 w-4" />
              Sensors
            </div>
            <div className="font-semibold">
              {activeSensors}/{totalSensors} Active
            </div>
          </div>

          {/* Last Update */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Clock className="h-4 w-4" />
              Last Update
            </div>
            <div className="font-semibold text-sm">
              {lastUpdateTime ? format(lastUpdateTime, 'MMM d, h:mm a') : 'No data'}
            </div>
          </div>

          {/* Alerts */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <AlertTriangle className="h-4 w-4" />
              Alerts
            </div>
            <div>
              {alertCount > 0 ? (
                <Badge variant="destructive" className="gap-1">
                  <TrendingUp className="h-3 w-3" />
                  {alertCount} Alert{alertCount !== 1 ? 's' : ''}
                </Badge>
              ) : (
                <Badge variant="outline" className="gap-1">
                  <Zap className="h-3 w-3" />
                  No Alerts
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Additional Info */}
        {(device.description || device.mac_address) && (
          <>
            <Separator className="my-4" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {device.description && (
                <div>
                  <span className="font-medium text-muted-foreground">Description: </span>
                  <span>{device.description}</span>
                </div>
              )}
              {device.mac_address && (
                <div>
                  <span className="font-medium text-muted-foreground">MAC Address: </span>
                  <span className="font-mono">{device.mac_address}</span>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
